import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, Music2, Video, Image as ImageIcon, Camera, Link as LinkIcon, Check, Youtube } from "lucide-react";
import { CATEGORIES } from "../lib/categories";
import { detectExternalLink } from "../lib/externalLinks";
import { EmbedPlayer, ProviderBadge } from "../components/EmbedPlayer";

// Genre presets per category
const GENRE_SETS = {
  music: ["Afrobeat", "Rap", "Hira Gasy", "Gospel", "Salegy", "R&B", "Jazz", "Coupé-Décalé", "Zouk", "Reggae", "Soul", "Pop", "Electro", "Zouglou"],
  humor: ["Stand-up", "Sketch", "Parodie", "Improvisation", "Vlog humour", "Format court"],
  cinema: ["Court-métrage", "Web série", "Documentaire", "Clip", "Vlog", "Making-of", "Teaser", "Fiction"],
  dance: ["Hip-hop", "Danse traditionnelle", "Contemporain", "Afro danse", "Battle", "Chorégraphie", "Ballet", "Salegy danse"],
  art: ["Peinture", "Dessin", "Photographie", "Graphisme", "Sculpture", "Street art", "Illustration", "Art contemporain"],
  fashion: ["Défilé", "Shooting", "Lookbook", "Collection", "Street style", "Editorial", "Accessoires"],
  food: ["Cuisine malgache", "Street food", "Pâtisserie", "Végétarien", "Recette rapide", "Plat familial", "Cuisine africaine", "Boissons", "Épices & marinades", "Tutoriel"],
  other: ["Slam", "Poésie", "Conte", "Magie", "Animation", "Performance", "Théâtre"],
};

// Allowed media types per category
const TYPE_SETS = {
  music: ["audio", "video"],
  humor: ["video", "audio"],
  cinema: ["video"],
  dance: ["video"],
  art: ["image"],
  fashion: ["image", "video"],
  food: ["video", "image"],
  other: ["video", "audio", "image"],
};

const TYPE_META = {
  audio: { label: "Audio", icon: Music2, accept: "audio/*", desc: "mp3, wav, flac, m4a", maxTip: "max 50MB" },
  video: { label: "Vidéo", icon: Video, accept: "video/mp4,video/x-m4v,video/quicktime,video/webm,.mp4,.m4v,.mov,.webm", desc: "mp4 H.264, webm, m4v, mov", maxTip: "max 100 Mo" },
  image: { label: "Image", icon: Camera, accept: "image/*", desc: "jpg, png, webp", maxTip: "max 20MB" },
};

const COUNTRIES = ["Madagascar", "Sénégal", "Nigeria", "Côte d'Ivoire", "Cameroun", "RDC", "Mali", "Afrique du Sud", "Kenya", "Ghana", "Maroc", "Tunisie"];
const LANGUAGES = ["Malgache", "Français", "Anglais", "Wolof", "Lingala", "Swahili", "Yoruba", "Arabe", "—"];

const CATEGORY_CTA = {
  music: "Partagez votre son avec le monde.",
  humor: "Faites rire le continent.",
  cinema: "Montrez votre talent d'auteur d'images.",
  dance: "Exprimez-vous par le mouvement.",
  art: "Exposez vos créations visuelles.",
  fashion: "Faites défiler votre style.",
  food: "Partagez vos recettes et saveurs gasy 🍲",
  other: "Révélez votre talent.",
};

export const UploadPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState("music");
  const genres = GENRE_SETS[category] || GENRE_SETS.music;
  const allowedTypes = TYPE_SETS[category] || ["audio", "video"];

  const [form, setForm] = useState({
    title: "",
    description: "",
    genre: GENRE_SETS.music[0],
    country: COUNTRIES[0],
    language: LANGUAGES[0],
    track_type: "audio",
  });
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState("file"); // "file" | "link"
  const [externalUrl, setExternalUrl] = useState("");

  const preview = useMemo(() => detectExternalLink(externalUrl), [externalUrl]);

  // Update genre and track_type when category changes
  const onCategoryChange = (c) => {
    setCategory(c);
    const newGenres = GENRE_SETS[c] || [];
    const newTypes = TYPE_SETS[c] || [];
    setForm((f) => ({
      ...f,
      genre: newGenres[0] || "Autre",
      track_type: newTypes[0] || "video",
    }));
    setFile(null);
  };

  const typeMeta = TYPE_META[form.track_type] || TYPE_META.video;

  if (!user) {
    return (
      <div className="px-6 py-20 text-center">
        <p className="text-white/60 mb-4">Vous devez être connecté pour publier.</p>
        <button onClick={() => navigate("/login")} className="btn-primary" data-testid="upload-login-redirect">Se connecter</button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let data;
      if (source === "link") {
        if (!preview) { toast.error("Lien non reconnu ou invalide"); setUploading(false); return; }
        const payload = {
          title: form.title,
          description: form.description,
          category,
          genre: form.genre,
          country: form.country,
          language: form.language,
          external_url: externalUrl.trim(),
        };
        ({ data } = await api.post("/tracks/publish-link", payload));
      } else {
        if (!file) { toast.error("Sélectionnez un fichier"); setUploading(false); return; }
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", category);
        fd.append("genre", form.genre);
        fd.append("country", form.country);
        fd.append("language", form.language);
        fd.append("track_type", form.track_type);
        fd.append("file", file);
        if (cover) fd.append("cover", cover);
        ({ data } = await api.post("/tracks/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      }
      toast.success("Publication réussie !");
      navigate(`/track/${data.track_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la publication");
    } finally {
      setUploading(false);
    }
  };

  const cta = CATEGORY_CTA[category] || "Publiez votre création.";

  return (
    <div className="px-4 md:px-12 py-10 max-w-4xl mx-auto pb-32" data-testid="upload-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Publier</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-10">{cta}</h1>

      <form onSubmit={submit} className="space-y-7">
        {/* Category selector */}
        <div>
          <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Catégorie *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onCategoryChange(c.key)}
                className={`flex items-center gap-2 px-3 py-3 rounded-xl border text-left transition-all ${
                  category === c.key ? "border-brand-red bg-brand-red/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                }`}
                data-testid={`upload-category-${c.key}`}
              >
                <span className="text-xl">{c.emoji}</span>
                <span className="text-sm font-semibold">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Type selector - dynamic per category (only for file mode) */}
        {source === "file" && allowedTypes.length > 1 && (
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Type de média *</label>
            <div className="grid grid-cols-3 gap-3">
              {allowedTypes.map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setForm({ ...form, track_type: t }); setFile(null); }}
                    className={`p-5 rounded-xl border transition-all text-left ${
                      form.track_type === t ? "border-brand-red bg-brand-red/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                    }`}
                    data-testid={`type-${t}`}
                  >
                    <div className={`mb-2 ${form.track_type === t ? "text-brand-red" : "text-white/70"}`}><Icon size={20} /></div>
                    <p className="font-semibold">{meta.label}</p>
                    <p className="text-xs text-white/50 mt-1">{meta.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Source toggle: File vs External Link */}
        <div>
          <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Source *</label>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              type="button"
              onClick={() => setSource("file")}
              className={`p-4 rounded-xl border text-left transition-all ${source === "file" ? "border-brand-red bg-brand-red/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
              data-testid="src-file"
            >
              <UploadCloud size={18} className={source === "file" ? "text-brand-red mb-2" : "text-white/60 mb-2"} />
              <p className="text-sm font-semibold">Téléverser un fichier</p>
              <p className="text-[11px] text-white/50 mt-0.5">Audio, vidéo ou image depuis votre appareil</p>
            </button>
            <button
              type="button"
              onClick={() => setSource("link")}
              className={`p-4 rounded-xl border text-left transition-all ${source === "link" ? "border-brand-red bg-brand-red/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
              data-testid="src-link"
            >
              <LinkIcon size={18} className={source === "link" ? "text-brand-red mb-2" : "text-white/60 mb-2"} />
              <p className="text-sm font-semibold">Lien externe</p>
              <p className="text-[11px] text-white/50 mt-0.5">YouTube, TikTok, Vimeo, SoundCloud...</p>
            </button>
          </div>
        </div>

        {source === "file" ? (
          <>
            {/* File dropzone */}
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Fichier *</label>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-white/15 hover:border-brand-red/50 rounded-2xl p-10 cursor-pointer transition-colors bg-white/[0.02]">
                <UploadCloud size={32} className="text-brand-red mb-3" />
                <p className="text-sm font-semibold">{file ? file.name : "Glissez-déposez ou cliquez pour choisir"}</p>
                <p className="text-xs text-white/50 mt-1">
                  {typeMeta.desc} ({typeMeta.maxTip})
                </p>
                <input
                  type="file"
                  accept={typeMeta.accept}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) { setFile(null); return; }
                    if (form.track_type === "video") {
                      const ext = (f.name.split(".").pop() || "").toLowerCase();
                      const blocked = ["avi", "mkv", "wmv", "flv", "3gp", "rm", "rmvb", "ts", "vob"];
                      if (blocked.includes(ext)) {
                        toast.error(`Format .${ext} non supporté pour la lecture web. Convertis en MP4 H.264 d'abord.`);
                        e.target.value = "";
                        return;
                      }
                      if (f.size > 100 * 1024 * 1024) {
                        toast.error(`Fichier trop lourd (${(f.size/1024/1024).toFixed(1)} Mo). Maximum : 100 Mo.`);
                        e.target.value = "";
                        return;
                      }
                    }
                    setFile(f);
                  }}
                  className="hidden"
                  data-testid="upload-file-input"
                />
              </label>
              {form.track_type === "video" && (
                <div className="mt-3 rounded-2xl bg-gradient-to-br from-amber-500/[0.06] via-amber-500/[0.04] to-orange-500/[0.06] border border-amber-400/30 p-4 sm:p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-300 text-base">📹</div>
                    <div>
                      <p className="font-black text-amber-200 text-sm leading-tight">Quels formats vidéo acceptés ?</p>
                      <p className="text-[11px] text-amber-100/80 mt-0.5">Pour une lecture fluide sur mobile, ordi et tablette</p>
                    </div>
                  </div>

                  {/* Tableau formats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-amber-100/90 mb-3">
                    <div className="flex items-center gap-2"><span className="text-green-400 text-base leading-none">✓</span> <strong>.mp4</strong> (H.264) — recommandé</div>
                    <div className="flex items-center gap-2"><span className="text-green-400 text-base leading-none">✓</span> <strong>.webm</strong> (VP8/VP9)</div>
                    <div className="flex items-center gap-2"><span className="text-green-400 text-base leading-none">✓</span> <strong>.m4v</strong> (H.264)</div>
                    <div className="flex items-center gap-2"><span className="text-yellow-400 text-base leading-none">~</span> <strong>.mov</strong> (à convertir en .mp4 si possible)</div>
                    <div className="flex items-center gap-2 col-span-1 sm:col-span-2 pt-1 border-t border-amber-400/20 mt-1"><span className="text-red-400 text-base leading-none">✗</span> <span className="text-red-200/90"><strong>Refusés :</strong> .avi, .mkv, .wmv, .flv, et tout fichier en codec HEVC/H.265</span></div>
                  </div>

                  {/* Règles */}
                  <div className="text-[11px] text-amber-100/80 space-y-1 border-t border-amber-400/20 pt-3">
                    <p><strong className="text-amber-200">📏 Taille max :</strong> 100 Mo</p>
                    <p><strong className="text-amber-200">⚠️ Vidéo de ton téléphone :</strong> par défaut, les Android et iPhone récents enregistrent en <strong>HEVC/H.265</strong> qui n'est <strong>pas lisible sur le web</strong>. Convertis-la d'abord :</p>
                    <ul className="list-disc pl-5 space-y-0.5 text-amber-100/75">
                      <li>📱 <strong>InShot</strong> (gratuit, Android/iOS) → Exporter → Qualité 1080p H.264</li>
                      <li>💻 <strong>VLC</strong> (gratuit, ordi) → Convertir/Enregistrer → Profil "Video H.264 + MP3 (MP4)"</li>
                      <li>🎬 <strong>HandBrake</strong> (gratuit) → Preset "Fast 1080p30"</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Cover (only for non-image types) */}
            {form.track_type !== "image" && (
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Pochette / aperçu (optionnel)</label>
                <label className="flex items-center gap-3 border border-white/10 hover:border-brand-orange/50 rounded-xl p-4 cursor-pointer transition-colors bg-white/[0.02]">
                  <ImageIcon size={18} className="text-brand-orange" />
                  <span className="text-sm">{cover ? cover.name : "Choisir une image (jpg, png)"}</span>
                  <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} className="hidden" data-testid="upload-cover-input" />
                </label>
              </div>
            )}
          </>
        ) : (
          <>
            {/* External link input */}
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Lien YouTube / TikTok / Vimeo / SoundCloud *</label>
              <div className="relative">
                <LinkIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://youtu.be/... ou https://www.tiktok.com/@... ou https://soundcloud.com/..."
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-brand-red/50"
                  data-testid="upload-link-input"
                />
              </div>
              {externalUrl && (
                <p className={`text-xs mt-2 flex items-center gap-1 ${preview ? "text-green-400" : "text-amber-400"}`}>
                  {preview ? (
                    <><Check size={12} /> Détecté : <ProviderBadge provider={preview.provider} /></>
                  ) : (
                    <>Lien non reconnu. Supportés : YouTube, Vimeo, TikTok, SoundCloud, Dailymotion, Instagram, Facebook.</>
                  )}
                </p>
              )}
              <p className="text-[11px] text-white/40 mt-2 flex items-center gap-1.5">
                <Youtube size={12} /> Le contenu sera lu directement depuis la plateforme d'origine (pas de stockage sur Voamada).
              </p>
            </div>

            {/* Embed preview */}
            {preview && (
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">Aperçu</label>
                <EmbedPlayer track={{ title: form.title || "Aperçu", external_embed: preview.embedUrl, external_provider: preview.provider }} />
              </div>
            )}
          </>
        )}

        {/* Title + Desc */}
        <Field label="Titre *">
          <input
            required
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50"
            placeholder="Ex: Tiana - Mandeha"
            data-testid="upload-title-input"
          />
        </Field>

        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50 min-h-[100px]"
            placeholder="Racontez l'histoire derrière cette création..."
            data-testid="upload-desc-input"
          />
        </Field>

        <div className="grid md:grid-cols-3 gap-4">
          <Select label={category === "music" ? "Genre" : category === "humor" ? "Format" : "Type"} value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} items={genres} testid="upload-genre" />
          <Select label="Pays" value={form.country} onChange={(v) => setForm({ ...form, country: v })} items={COUNTRIES} testid="upload-country" />
          <Select label="Langue" value={form.language} onChange={(v) => setForm({ ...form, language: v })} items={LANGUAGES} testid="upload-language" />
        </div>

        <button type="submit" disabled={uploading} className="btn-primary w-full md:w-auto inline-flex items-center gap-2 disabled:opacity-50" data-testid="upload-submit-btn">
          <UploadCloud size={16} /> {uploading ? "Publication en cours..." : "Publier"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">{label}</label>
    {children}
  </div>
);

const Select = ({ label, value, onChange, items, testid }) => (
  <Field label={label}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50"
      data-testid={testid}
    >
      {items.map((i) => <option key={i} value={i} className="bg-brand-dark">{i}</option>)}
    </select>
  </Field>
);

export default UploadPage;
